import { useEvent } from '@shared-core/hooks/use-event'
import { AppLogger } from '@shared-core/services/logger'
import { useHaptic } from '@shared-ui/platform/hooks/use-haptic'
import type { HapticType } from '@shared-ui/platform/hooks/use-haptic/types'
import { forwardRef, type PropsWithChildren, type Ref, useEffect } from 'react'
import {
  Platform,
  type StyleProp,
  type TouchableOpacityProps,
  TouchableWithoutFeedback,
  type ViewStyle,
} from 'react-native'
import Animated, { cancelAnimation, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated'
import { useCreateStyles } from './styles'

export type ActiveTouchActionProps = PropsWithChildren<{
  onPress?: TouchableOpacityProps['onPress']
  onPressIn?: TouchableOpacityProps['onPressIn']
  onLongPress?: () => void
  style?: StyleProp<ViewStyle>
  disabled?: boolean
  isDisabledAnimation?: boolean
  hitSlop?: TouchableOpacityProps['hitSlop']
  testID?: TouchableOpacityProps['testID']
  className?: string
  hapticType?: HapticType
  isPressHapticDisabled?: boolean
  // На web разрешает выделять текст внутри (для копирования), не ломая тап
  isSelectable?: boolean
}>

const logger = AppLogger.fork({ name: 'ActiveTouchAction' })

export const ActiveTouchAction = forwardRef(function ActiveTouchActionFn(
  props: ActiveTouchActionProps,
  ref: Ref<Animated.View>,
) {
  const styles = useCreateStyles()
  const pressed = useSharedValue(1)
  const haptic = useHaptic()
  const hasPressHandler = Boolean(props.onPress)

  const triggerPressHaptic = useEvent(() => {
    if (Platform.OS !== 'web' || props.disabled || props.isPressHapticDisabled || !hasPressHandler) {
      return
    }

    haptic(props.hapticType ?? 'impactLight')
  })

  // тут дополнительно проверяем выключена ли анимация, если нет то анимируем нажатие
  const onTouchIn = useEvent((event) => {
    try {
      if (props.onPressIn) {
        props.onPressIn(event)
      }

      if (props.isDisabledAnimation === undefined) {
        pressed.value = withTiming(0.95, { duration: props.onLongPress ? 320 : 160 })
      }
    } catch (e) {
      console.log(e)
      logger.error('failed touch in')
    }
  })

  const onPressOut = useEvent(() => {
    try {
      if (props.isDisabledAnimation === undefined) {
        pressed.value = withTiming(1, { duration: props.onLongPress ? 420 : 160 })
      }
    } catch {
      logger.error('failed touch out')
    }
  })

  const handlePress = useEvent((event) => {
    if (!props.onPress) {
      return
    }

    triggerPressHaptic()

    props.onPress(event)
  })

  const handleLongPress = useEvent(() => {
    if (props.onLongPress) {
      haptic('impactMedium')
      props.onLongPress()
    }
  })

  const animatedStyles = useAnimatedStyle(() => ({
    transform: [{ scale: pressed.get() }],
  }))
  const classNameProps = props.className ? ({ className: props.className } as Record<string, unknown>) : {}

  useEffect(() => {
    return () => cancelAnimation(pressed)
  }, [])

  return (
    <TouchableWithoutFeedback
      onPressOut={onPressOut}
      onPressIn={onTouchIn}
      onPress={handlePress}
      onLongPress={props.onLongPress ? handleLongPress : undefined}
      delayLongPress={250}
      disabled={props.disabled}
      hitSlop={props.hitSlop}
      style={props.style}
      testID={props.testID}
    >
      <Animated.View
        {...classNameProps}
        ref={ref}
        style={[
          animatedStyles,
          props.style,
          styles.action,
          props.isSelectable && styles.actionSelectable,
          props.disabled && styles.actionDisabled,
        ]}
      >
        {props.children}
      </Animated.View>
    </TouchableWithoutFeedback>
  )
})
